import { IVoiceType, Lang } from "types";
import { IAudioHandles } from "./handleTypes";
import type { RealtimeEditorStatus } from "../../api/fastApi/realtimeEditor";
import type { SubtitleRequestState } from "./useSubtitleRequest";
import type { ManualOnlyState } from "../../api/fastApi/manualOnly";
import type { CueSpeaker } from "./cueSpeaker";

export interface IAudioStateProvider {
    state: IAudioState;
}
export interface IAudioHandlesProvider {
    handles: IAudioHandles;
}

export interface IAudioState {
    isDragging: boolean;
    zoomFragment: number[];
    audioDuration: number;
    playing: boolean;
    /** Озвучка + видео играют, оригинальное аудио заглушено (клик по правой колонке) */
    voiceoverOnlyPlayback: boolean;
    playbackTime: number;
    activeSegmentLength: number;
    mediaId: string;
    mp3: string | undefined;
    translationTrackAll: Partial<ITracksAll>;
    subtitlesDataAll: Partial<ISubtitlesDataAll>;
    subtitlesRequest: SubtitleRequestState;
    name: string;
    lang: ILang;
    toLang: string;
    video: string | undefined;
    playingFragment: string | null;
    playingFragmentRestart: number;
    playingFragmentNext: AudioTrack | null;
    volumeOriginal: number;
    volumeTranslated: number;
    zoomLevel: number;
    changedRecently: string[];
    changed: string[];
    changedTranslation: string[];
    changedRus: string[];
    newData: AudioTrack[];
    scrubberWidth: number;
    playStart: number | undefined;
    playOriginalStart: number;
    folder: number;
    playbackRate: number;
    foldersTotal: number;
    waitingCut: string[];
    editingSubtitles: string | null;
    scrollTo: number | null;
    videoUrl: string;
    lastFragmentTrimmed: boolean;
    subtitleMode: boolean;
    videoPlayerType: string;
    savingStatus: boolean;
    russian: boolean;
    trackSelected: string | null;
    /** Translated-led merge has its own selection so it can't cross with a RU merge. */
    translatedTrackSelected: string | null;
    /** Last translated-led operation error (backend 409/422 text, shown verbatim). */
    translatedOpError: string | null;
    /**
     * «Наложение снято…»: какие озвучки машина сама отодвинула позже (режим «Без автоправок»,
     * правило владельца 01.10.2026). Сообщение, а не отказ — своя нейтральная полоса.
     */
    overlapPushNotice: string | null;
    /** Реплика, которую надо выделить и отдать ей фокус, как только она появится (новая «+»). */
    focusCue: FocusCueRequest | null;
    /** Сколько правок сервер не принял (они ждут в несохранённых; их можно сбросить). */
    refusedEdits: number;
    durationFull: number;
    showOriginalLanguage: boolean;
    subtitleEditing: boolean;
    captionsSize: Partial<Record<ILang, number>>;
    translatedSubtitleId: null | string;
    editedRecommendationSubtitle: string | null;
    startPlaying: boolean;
    voicingSpaces: ISpace[];
    voicingSpacesDeleted: string[];
    voicingSpacesOn: boolean;
    voicingSpacesMinimum: number;
    voicingSpacesMinimumOffset: number;
    voicingSpacesAllPages: Record<string, ISpace[]>;
    originalLang: ILang;
    catVideoFolderUrl: Record<string, string>;
    playSavedTrack: boolean;
    changedAudioTrackIds: string[] | null;
    updatedTranslation: boolean;
    editedTrackId: string | null;
    disableMovementRestrictions: boolean;
    isDirty: boolean;
    autoSaveStatus: AutoSaveStatus;
    selectedTranslatedTrackIds: SelectedTranslatedTrack[];
    selectedOriginalTrackIds: SelectedTranslatedTrack[];
    lectureName: string;
    lectureStatus: IStageStatus[];
    useCheckedVoiceoverSynthesis: boolean;
    /** Clicking a voicing silences the lecturer (the Ctrl/Cmd modifier, made a setting). */
    muteOriginalOnVoicingClick: boolean;
    /** Показывать под временем реплики её время от начала всего видео, а не только папки. */
    showGlobalTime: boolean;
    /** Везти ленту за воспроизведением, чтобы звучащая реплика оставалась на экране. */
    followPlayback: boolean;
    realtimeEditorStatus: RealtimeEditorStatus | null;
    /**
     * «Без автоправок — только ручная работа» для открытой пары: правка одной реплики
     * сохраняет одну реплику; соседи сдвигаются, только если на них наехала озвучка, — позже,
     * на её конец + 50 мс (правило владельца 01.10.2026), раньше никогда, без подгонки скорости.
     * false и тогда, когда флаг ещё не известен, — поведение прежнее.
     */
    manualOnly: boolean;
    /** Что последним ответил сервер про флаг пары (кто и когда включил); null — не знаем. */
    manualOnlyState: ManualOnlyState | null;
    /** На экране пара языков — только к ней режим и относится. */
    manualOnlyApplicable: boolean;
    manualOnlyLoading: boolean;
    manualOnlyError: string | null;
    /**
     * Открыт живой эфир. Включённый режим останавливает его дубляж этой пары (сервер редактора
     * отказывает эфиру в подготовке и пересборке её папок) — выключатель спрашивает об этом
     * заранее и передаёт серверу подтверждение.
     */
    manualOnlyStream: boolean;
}

export interface IStageStatus {
    key: string;
    label: string;
    done: number;
    inProgress: number;
    total: number;
}

export type ILang = "en" | "ru" | "de" | "tr" | "ch" | "es" | "lit" | "ar";

export type ISubtitlesDataAll = Record<ILang, AudioFolderData[]>;
export type ITracksAll = Record<ILang, AudioTrack[]>;

export interface IChangedData {
    play: string;
    changed?: string[];
    changedTranslation?: string[];
    changedRus?: string[];
    changedReels?: string[];
}

export enum ChangedDataKeys {
    play = "play",
    changed = "changed",
    changedTranslation = "changedTranslation",
    changedRus = "changedRus",
    changedReels = "changedReels",
}

export enum LocalStorageKeys {
    captionSize = "captionSize",
    changedAudio = "changedAudio",
    pausedTime = "pausedTime",
    useCheckedVoiceoverSynthesis = "useCheckedVoiceoverSynthesis",
    muteOriginalOnVoicingClick = "muteOriginalOnVoicingClick",
    showGlobalTime = "showGlobalTime",
    followPlayback = "followPlayback",
    voiceoverV2RetryCount = "voiceoverV2RetryCount",
}

export enum ApiRoutes {
    move = "move",
    length = "length",
    move_rus = "move_rus",
    length_rus = "length_rus",
}

export enum MetadataToSubtitlesLanguages {
    english = "en-US",
    en = "en-US",
    tr = "tr-TR",
    chinese = "ch-CH",
    ch = "ch-CH",
    es = "es-ES",
    lit = "lit-lit",
    de = "de-de",
    ru = "ru-RU",
    ar = "ar-SA",
}

/**
 * Wire code for a language, falling back to the code itself.
 *
 * The enum above knows eight languages. Indexing it with anything else — kz, fr, pt, or the
 * `zh` that some lecture directories still use — yields `undefined`, `JSON.stringify` drops the
 * key, and the request reaches the backend naming no target language at all. That used to
 * resolve to the source master and quietly rewrite the original every other pair copies from.
 * The backend now refuses such a request outright, which turns the same bug into a visible
 * error; this keeps it from being sent in the first place.
 */
export const wireLang = (code?: string | null): MetadataToSubtitlesLanguages =>
    ((code && (MetadataToSubtitlesLanguages as Record<string, string>)[code]) || code || "") as MetadataToSubtitlesLanguages;

export interface AudioFolderData {
    folder_num: string;
    orig_mp3: string;
    orig_mp4?: string;
    prev_mp3_length_aggr: string;
    translations: AudioTrackData[];
    original_translations?: AudioTrackData[];
    /** Флаг «без автоправок» пары, если сервер кладёт его в данные папки. */
    manual_only?: boolean;
}

/**
 * Проверка короткой озвучки на слух (сервер, после синтеза текста до 3 слов на языке с латиницей):
 * Scribe расшифровывает дубль, его слова сравниваются со словами текста, при расхождении дубль
 * синтезируется заново (до 4 раз всего). ok: true — слова совпали; false — не совпали и после
 * всех попыток (оставлен последний или ближайший дубль); null — проверка недоступна (Scribe
 * ответил ошибкой), дубль оставлен без неё. heard — что слышно в последнем дубле, takes —
 * сколько дублей сделано.
 */
export interface VoicingCheck {
    ok: boolean | null;
    heard?: string;
    takes?: number;
}

export interface AudioTrackData {
    /** Версия wav дубля с сервера («время-размер»): новый дубль — новый адрес звука (кэш браузера). */
    file_v?: string;
    /**
     * Своя правка места реплики эфира ещё не в эфире: дорожка держит место из правки (savedTimingPins, проба P50m20g,
     * E7) и говорит «ждёт круга».
     */
    timing_pending?: boolean;
    id: string;
    start: number;
    end?: number;
    nextTrackId?: string;
    add_background?: boolean;
    captions: string;
    end_rus: string | null;
    end_rus_milli: number;
    file?: string;
    folder_num: string;
    lang: MetadataToSubtitlesLanguages;
    length: number;
    new_start: number;
    shifted_start: number;
    speed?: number;
    start_rus: string | null;
    start_rus_milli: number;
    voice?: IVoiceType;
    words_scores?: number | null;
    translationStart?: number;
    /** Сервер редактора: время реплики испорчено (длина ≤ 0 или отрицательные точки). */
    timing_invalid?: boolean;
    /** Сервер редактора: что именно испорчено, его словами («длина -1820.0», «конец -153841»). */
    timing_problem?: string;
    /**
     * Пара «Без автоправок»: сколько звучит дубль реплики по мерке сервера (длина самого wav в
     * целых мс); null — сервер её озвученной не считает (нет wav или снята с озвучки). Нет поля —
     * старый сервер. По нему раздвижка редактора решает, есть ли наложение (см. dubSoundingMs).
     */
    dub_ms?: number | null;
    /** Итог проверки дубля на слух (см. VoicingCheck); нет поля — не проверялся. */
    voicing_check?: VoicingCheck;
    /**
     * Пометка дубля нового голоса эфира (TAG `stream_voice`): роль и имя голоса (`male1`/Michael,
     * `female`/Matilda; у лектора роли нет). По ней строка называет, кто говорит (cueSpeaker).
     */
    _v4_voice?: { role?: string | null; name?: string | null; profile?: string | null };
    /**
     * Кусок фразы из раскладки дубляжа по фразам (split_voicing): id реплики-родителя. У куска
     * своя часть перевода и своя озвучка, а русский текст — обычно вся фраза родителя. Сервер
     * присылает поле в json и оригинала, и перевода.
     */
    split_parent?: string;
    /**
     * Реплика снята с озвучки (⊘, `/create_skip_subtitle`): в видео не попадают ни её перевод, ни
     * озвучка. Возвращается ♪ (новый сервер снимает метку при озвучке) или «вернуть».
     */
    skip?: boolean;
    /**
     * Текст снятой реплики, отложенный новым сервером (у самой реплики `captions: ""`, чтобы его не
     * взяла сборка): его вернут ♪ и «вернуть».
     */
    skipped_captions?: string;
    /**
     * Озвучка без реплики оригинала в папке (её реплику оригинала удалили, а перевод и wav
     * остались): пометку ставит редактор, сравнив id с репликами оригинала (orphanVoicings.ts),
     * сервер её не присылает и на сервер она не уходит. Такая озвучка не звучит (voicingSilenced),
     * и с ней можно только удалить (4119e839, ru_es, папка 1, 1:22, «Así es.», 02.10.2026).
     */
    orphan_voicing?: boolean;
}

export type AudioTrack = AudioTrackData & {
    original_length: number;
    trimmed: number | null;
    trimmed_start: number | null;
    original_start: number;
    original_shifted_start: number;
    original_id: string;
    [key: string]: any;
    translation?: string;
    /** «Без автоправок»: на сколько мс хвост заходит на следующую озвучку сверх допуска сборки. */
    manual_overlap_ms?: number;
    // Куски одной фразы (markPhrasePieces): номер куска по времени и сколько их у фразы в папке.
    // Одинокий кусок (остальные удалены) не помечается — он выглядит обычной репликой.
    /** Номер куска фразы, с 1. */
    piece_index?: number;
    /** Сколько кусков у фразы в этой папке (≥ 2). */
    piece_count?: number;
    /**
     * Русский текст куска тот же, что у более раннего куска этой фразы: колонка оригинала не
     * повторяет его, а пишет «продолжение фразы».
     */
    piece_repeats_text?: boolean;
    /** Русский текст куска тот же, что у другого куска фразы (обычно — вся фраза у всех кусков). */
    piece_shares_text?: boolean;
    /**
     * Перевод куска повторяет перевод этих кусков фразы (номера с 1; схожесть ≥ 0,85) — это
     * настоящий повтор в дубляже, а не своя часть перевода (043f05ea, ru_en, папка 24).
     */
    piece_dub_repeats?: number[];
    /** …повторяет их переводы ВМЕСТЕ: в куске весь перевод фразы, у остальных — её части. */
    piece_dub_repeats_whole?: boolean;
    // Строка оригинала несёт то, что ей нужно знать о паре перевода (updateRusData): ♪ и 🔊
    // заранее говорят, откажет ли сервер озвучки, а метка «⚠ время» — что сломано.
    /** У строки есть json перевода на сервере. */
    has_translation?: boolean;
    /** Время перевода испорчено (метка сервера timing_invalid или то же правило здесь). */
    translation_timing_invalid?: boolean;
    /** Что испорчено во времени перевода — словами сервера, если он их прислал. */
    translation_timing_problem?: string;
    /** Испорчено само окно перевода (отрицательные место или слот) — это чинит «вернуть окно». */
    translation_window_broken?: boolean;
    /** Место озвучки перевода (его start), как его читает сервер; null — не время. */
    translation_start?: number | null;
    /** Начало слота перевода (start_rus_milli); нет поля — нет ключа в json. */
    translation_slot_start?: number | null;
    /** Конец слота перевода (end_rus_milli, иначе строка end_rus); null — не время. */
    translation_slot_end?: number | null;
    /** Итог проверки дубля перевода на слух: ok false — ♪ строки предупреждает, что слышно. */
    translation_voicing_check?: VoicingCheck;
    /** Перевод снят с озвучки (`skip` в json перевода): строка перевода говорит это словами. */
    translation_skip?: boolean;
    /** Текст снятого перевода, отложенный сервером (`skipped_captions`); только у снятой. */
    translation_skipped_text?: string;
    /** У перевода есть файл озвучки (`file` в json): ⊘ спрашивает, прежде чем её снять. */
    translation_voiced?: boolean;
    /** Кто говорит перевод — лектор или зал и каким голосом (подпись строки, проба P50m10d). */
    translation_speaker?: CueSpeaker | null;
};

export type AutoSaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

/** id — имя реплики на диске (original_id); at — отличает повторную просьбу о той же реплике. */
export interface FocusCueRequest {
    id: string;
    folder: number;
    at: number;
}

export interface SelectedTranslatedTrack {
    ind: number;
    track: AudioTrack;
}

export interface DirtyTrackChange {
    lang: ILang;
    trackId: string;         // original_id
    folderNum: number;
    fields: Partial<AudioTrackData>;
    /**
     * Запись — только сдвиг, сделанный машиной: наложение, снятое в режиме «Без автоправок»
     * (правило владельца 01.10.2026). Уходит отдельным запросом с пометкой в заголовке, чтобы
     * журнал сервера не приписал его переводчику. Правка той же реплики человеком — другая
     * запись (свой ключ, см. dirtyKeys): они не сливаются; его новое место снимает сдвиг.
     */
    origin?: "manual_overlap_push";
    /**
     * Правка времени в редакторе живого эфира (liveTiming.ts): `start` / `length` — только то, что
     * человек сдвинул или растянул сам. Уходит полем `stream_timing`, а не слотом
     * `start_rus_milli`/`end_rus_milli`: сервер эфира пишет одно место дубля и заводит заявку круга
     * правки; длину дубля и слот не переписывает.
     */
    liveTiming?: boolean;
}

export type ICaptionSizeState = Partial<Record<ILang, number>>;

export interface ISpace {
    start: number;
    end: any;
    nextTrackId?: string;
}
